// Store dei PROFIT TASKS (task con lato economico)
import { create } from 'zustand';
import type { ProfitTask, ProfitStatus, Priority } from '@/types';
import { getDatabase } from '@/lib/db';
import { genId, nowISO } from '@/utils';
import { pushProfitToCloud, deleteProfitFromCloud } from './syncHelpers';

interface ProfitTasksState {
  tasks: ProfitTask[];
  loading: boolean;
  load: () => Promise<void>;
  create: (data: Partial<ProfitTask>) => Promise<ProfitTask>;
  update: (id: string, patch: Partial<ProfitTask>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  move: (id: string, status: ProfitStatus, position: number) => Promise<void>;
  reorder: (status: ProfitStatus, orderedIds: string[]) => Promise<void>;
  replaceAll: (tasks: ProfitTask[]) => Promise<void>;
}

export const useProfitTasksStore = create<ProfitTasksState>((set, get) => ({
  tasks: [],
  loading: false,

  load: async () => {
    set({ loading: true });
    try {
      const db = getDatabase();
      const tasks = await db.getAllProfitTasks();
      set({ tasks, loading: false });
    } catch (e) {
      console.error('[profit] load error', e);
      set({ loading: false });
    }
  },

  create: async (data) => {
    const now = nowISO();
    const maxPos = get().tasks.filter((t) => t.status === (data.status ?? 'lead')).reduce((m, t) => Math.max(m, t.position), -1);
    const task: ProfitTask = {
      id: genId(),
      title: data.title ?? '',
      description: data.description ?? '',
      status: (data.status ?? 'lead') as ProfitStatus,
      priority: (data.priority ?? 'medium') as Priority,
      client_name: data.client_name ?? '',
      expected_revenue: data.expected_revenue ?? 0,
      actual_revenue: data.actual_revenue ?? 0,
      cost: data.cost ?? 0,
      currency: data.currency ?? 'EUR',
      due_date: data.due_date ?? null,
      tags: data.tags ?? [],
      position: data.position ?? maxPos + 1,
      created_at: now,
      updated_at: now,
    };
    const db = getDatabase();
    await db.upsertProfitTask(task);
    set({ tasks: [...get().tasks, task] });
    pushProfitToCloud(task).catch(() => {});
    return task;
  },

  update: async (id, patch) => {
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    const updated: ProfitTask = { ...current, ...patch, updated_at: nowISO() };
    const db = getDatabase();
    await db.upsertProfitTask(updated);
    set({ tasks: get().tasks.map((t) => (t.id === id ? updated : t)) });
    pushProfitToCloud(updated).catch(() => {});
  },

  remove: async (id) => {
    const db = getDatabase();
    await db.deleteProfitTask(id);
    set({ tasks: get().tasks.filter((t) => t.id !== id) });
    deleteProfitFromCloud(id).catch(() => {});
  },

  move: async (id, status, position) => {
    await get().update(id, { status, position });
  },

  reorder: async (status, orderedIds) => {
    const db = getDatabase();
    const tasks = get().tasks;
    const updates: ProfitTask[] = [];
    orderedIds.forEach((id, idx) => {
      const t = tasks.find((x) => x.id === id);
      if (t && (t.position !== idx || t.status !== status)) {
        const updated: ProfitTask = { ...t, status, position: idx, updated_at: nowISO() };
        updates.push(updated);
      }
    });
    for (const u of updates) await db.upsertProfitTask(u);
    set({
      tasks: tasks.map((t) => {
        const u = updates.find((x) => x.id === t.id);
        return u ?? t;
      }),
    });
  },

  replaceAll: async (tasks) => {
    const db = getDatabase();
    await db.replaceAllProfitTasks(tasks);
    set({ tasks: await db.getAllProfitTasks() });
  },
}));
